import * as React from "react"
import { 
  FileText, 
  Image as ImageIcon, 
  FileSpreadsheet, 
  Folder, 
  File, 
  FileCode,
  MoreVertical,
  Star,
  Sparkles,
  Link2,
  LucideIcon
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

export type FileType = 'pdf' | 'image' | 'doc' | 'spreadsheet' | 'folder' | 'code' | 'text' | 'other'

export interface FileCardMenuItem {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  variant?: 'default' | 'destructive'
}

export interface FileCardProps {
  name: string
  type: FileType
  size?: string
  modifiedAt?: Date
  tags?: string[]
  starred?: boolean
  hasAiSummary?: boolean
  hasRelation?: boolean
  relationTooltip?: string
  selected?: boolean
  onClick?: () => void
  onStarClick?: () => void
  menuItems?: FileCardMenuItem[]
  className?: string
}

const FILE_ICONS: Record<FileType, LucideIcon> = {
  pdf: FileText,
  image: ImageIcon,
  spreadsheet: FileSpreadsheet,
  folder: Folder,
  code: FileCode,
  doc: FileText,
  text: FileText,
  other: File,
}

const FILE_COLORS: Record<FileType, string> = {
  pdf: 'text-red-500 bg-red-50',
  image: 'text-purple-500 bg-purple-50',
  spreadsheet: 'text-green-500 bg-green-50',
  folder: 'text-amber-500 bg-amber-50',
  code: 'text-blue-500 bg-blue-50',
  doc: 'text-blue-600 bg-blue-50',
  text: 'text-gray-600 bg-gray-50',
  other: 'text-gray-500 bg-gray-50',
}

export function FileCard({
  name,
  type,
  size,
  modifiedAt,
  tags = [],
  starred,
  hasAiSummary,
  hasRelation,
  relationTooltip,
  selected,
  onClick,
  onStarClick,
  menuItems,
  className,
}: FileCardProps) {
  const Icon = FILE_ICONS[type] || File
  const colorClass = FILE_COLORS[type] || FILE_COLORS.other

  return (
    <div 
      onClick={onClick}
      className={cn(
        "group relative flex flex-col p-4 rounded-xl border transition-all cursor-pointer",
        "bg-gradient-to-br from-[color:var(--agent-surface-from)] to-[color:var(--agent-surface-to)]",
        "hover:shadow-md",
        selected 
          ? "border-[color:var(--agent-accent)] ring-1 ring-[color:var(--agent-accent)] shadow-md" 
          : "border-[color:var(--agent-surface-border)] hover:border-[color:var(--agent-accent)]/50",
        className
      )}
    >
      {/* Top Actions */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        {onStarClick && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6 hover:bg-white/50 rounded-full"
            onClick={(e) => { e.stopPropagation(); onStarClick(); }}
          >
            <Star className={cn("w-3 h-3", starred ? "text-yellow-400 fill-yellow-400" : "text-gray-400")} />
          </Button>
        )}
        {menuItems && menuItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 hover:bg-white/50 rounded-full"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-3 h-3 text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuItems.map((item, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation()
                    item.onClick()
                  }}
                  className={cn(
                    item.variant === 'destructive' && "text-red-600 focus:text-red-600 focus:bg-red-50"
                  )}
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Icon */}
      <div className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110", 
        colorClass
      )}>
        <Icon className="w-6 h-6" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-800 truncate mb-1 group-hover:text-[color:var(--agent-accent)] transition-colors">
          {name}
        </h3>
        {(size || modifiedAt) && (
          <p className="text-[10px] text-gray-400 flex items-center gap-2">
            {size}{size && modifiedAt && ' • '}{modifiedAt?.toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Tags & Indicators */}
      {((tags && tags.length > 0) || hasAiSummary || hasRelation) && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-1 overflow-hidden">
            {(tags || []).slice(0, 2).map((tag, idx) => (
              <span key={idx} className="text-[9px] px-1.5 py-0.5 bg-white/60 text-gray-500 rounded-md truncate max-w-[60px]">
                {tag}
              </span>
            ))}
            {tags && tags.length > 2 && (
              <span className="text-[9px] px-1.5 py-0.5 bg-white/40 text-gray-400 rounded-md">
                +{tags.length - 2}
              </span>
            )}
          </div>
          
          <div className="flex gap-1">
            {hasAiSummary && (
              <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center" title="Tiene resumen IA">
                <Sparkles className="w-3 h-3 text-purple-500" />
              </div>
            )}
            {hasRelation && (
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center" title={relationTooltip}>
                <Link2 className="w-3 h-3 text-blue-500" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
